let analyticsInitialized = false;
let lastTrackedPath = '';

const getMeasurementId = () => (process.env.REACT_APP_GA_MEASUREMENT_ID || '').trim();
const isAnalyticsDebugEnabled = () => (process.env.REACT_APP_ANALYTICS_DEBUG || '').trim().toLowerCase() === 'true';
const MAX_LABEL_LENGTH = 120;

export const initAnalytics = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return false;
  }

  const measurementId = getMeasurementId();
  if (!measurementId) {
    return false;
  }

  if (analyticsInitialized && typeof window.gtag === 'function') {
    return true;
  }

  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() {
    window.dataLayer.push(arguments);
  };

  const scriptSrc = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  const hasScript = !!document.querySelector(`script[src="${scriptSrc}"]`);

  if (!hasScript) {
    const script = document.createElement('script');
    script.async = true;
    script.src = scriptSrc;
    document.head.appendChild(script);
  }

  window.gtag('js', new Date());
  window.gtag('config', measurementId, { send_page_view: false });
  analyticsInitialized = true;
  return true;
};

export const trackPageView = ({ path, title }) => {
  if (!path || path === lastTrackedPath) {
    return;
  }

  const debugEnabled = isAnalyticsDebugEnabled();
  const payload = {
    page_path: path,
    page_title: title || document.title,
    page_location: window.location.href
  };

  if (debugEnabled) {
    console.info('[Analytics Debug] page_view', payload);
  }

  if (!initAnalytics() || typeof window.gtag !== 'function') {
    if (debugEnabled) {
      console.info('[Analytics Debug] GA not initialized. Check REACT_APP_GA_MEASUREMENT_ID.');
    }
    return;
  }

  window.gtag('event', 'page_view', payload);

  lastTrackedPath = path;
};

const sanitizeLabel = (value) => {
  if (!value) {
    return '';
  }

  return value.replace(/\s+/g, ' ').trim().slice(0, MAX_LABEL_LENGTH);
};

const getElementLabel = (element) => {
  const ariaLabel = sanitizeLabel(element.getAttribute('aria-label'));
  if (ariaLabel) {
    return ariaLabel;
  }

  const title = sanitizeLabel(element.getAttribute('title'));
  if (title) {
    return title;
  }

  const text = sanitizeLabel(element.textContent);
  if (text) {
    return text;
  }

  const id = sanitizeLabel(element.id);
  if (id) {
    return id;
  }

  return element.tagName.toLowerCase();
};

const getElementType = (element) => element.tagName.toLowerCase();

export const trackClickEvent = (element) => {
  if (!element) {
    return;
  }

  const tagName = getElementType(element);
  const isButton = tagName === 'button';
  const isAnchor = tagName === 'a';

  if (!isButton && !isAnchor) {
    return;
  }

  const href = isAnchor ? element.getAttribute('href') || '' : '';
  const payload = {
    element_type: isButton ? 'button' : 'anchor',
    element_label: getElementLabel(element),
    element_id: sanitizeLabel(element.id),
    element_classes: sanitizeLabel(element.className),
    click_text: sanitizeLabel(element.textContent),
    click_url: href,
    click_target: isAnchor ? element.getAttribute('target') || '' : '',
    page_path: `${window.location.pathname}${window.location.search}${window.location.hash}`,
    page_title: document.title
  };

  const debugEnabled = isAnalyticsDebugEnabled();
  if (debugEnabled) {
    console.info('[Analytics Debug] click_event', payload);
  }

  if (!initAnalytics() || typeof window.gtag !== 'function') {
    if (debugEnabled) {
      console.info('[Analytics Debug] GA not initialized. Check REACT_APP_GA_MEASUREMENT_ID.');
    }
    return;
  }

  window.gtag('event', 'click_event', payload);
};
